import { Refine } from "@refinedev/core";
import routerProvider from "@refinedev/react-router";
import { type ReactNode, useMemo } from "react";
import { Navigate, Route, Routes } from "react-router";
import { AnalyticsRoutes } from "../analytics/AnalyticsRoutes";
import { useAnalyticsRuntimeOf } from "../analytics/admin-runtime";
import { AdminAnalyticsProvider } from "../analytics/analytics-context";
import { type AnalyticsRuntime, AnalyticsRuntimeProvider } from "../analytics/runtime";
import { developmentAuthProvider } from "../auth/development-auth-provider";
import { ProtectedBoundary } from "../auth/ProtectedBoundary";
import { supabaseAuthProvider } from "../auth/supabase-auth-provider";
import { createAdminCatalogRuntime } from "../catalog/admin-runtime";
import { CatalogRoutes } from "../catalog/CatalogRoutes";
import { createLafayetteDataProvider } from "../catalog/data-provider";
import { type CatalogRuntime, CatalogRuntimeProvider } from "../catalog/runtime";
import { getAdminPublicConfig } from "../config/public-config";
import { createAdminConversationsRuntime } from "../conversations/admin-runtime";
import { ConversationsRoutes } from "../conversations/ConversationsRoutes";
import { type ConversationsRuntime, ConversationsRuntimeProvider } from "../conversations/runtime";
import { createAdminMemoryRuntime } from "../customers/admin-runtime";
import { CustomersRoutes } from "../customers/CustomersRoutes";
import { type MemoryRuntime, MemoryRuntimeProvider } from "../customers/runtime";
import { createAdminDiagnosticsRuntime } from "../diagnostics/admin-runtime";
import { type DiagnosticsRuntime, DiagnosticsRuntimeProvider } from "../diagnostics/runtime";
import { createAdminHomeRuntime } from "../home/admin-runtime";
import { HomePage } from "../home/HomePage";
import { type HomeRuntime, HomeRuntimeProvider } from "../home/runtime";
import { createAdminImportsRuntime } from "../imports/admin-runtime";
import { createImportsDataProvider, IMPORTS_DATA_PROVIDER } from "../imports/data-provider";
import { ImportRoutes } from "../imports/ImportRoutes";
import { type ImportsRuntime, ImportsRuntimeProvider } from "../imports/runtime";
import { createAdminItineraryRuntime } from "../itinerary/admin-runtime";
import { ItineraryRoutes } from "../itinerary/ItineraryRoutes";
import { type ItineraryRuntime, ItineraryRuntimeProvider } from "../itinerary/runtime";
import { shellRoutePath } from "../navigation/offering";
import { createAdminOperationsRuntime } from "../operations/admin-runtime";
import { OperationsRoutes } from "../operations/OperationsRoutes";
import { type OperationsRuntime, OperationsRuntimeProvider } from "../operations/runtime";
import { AuthCallbackPage } from "../pages/AuthCallbackPage";
import { LoginPage } from "../pages/LoginPage";
import { PlaceholderPage } from "../pages/PlaceholderPage";
import { createAdminPaymentsRuntime } from "../payments/admin-runtime";
import { PaymentsRoutes } from "../payments/PaymentsRoutes";
import { type PaymentsRuntime, PaymentsRuntimeProvider } from "../payments/runtime";
import { createAdminPrivacyRuntime } from "../privacy/admin-runtime";
import { PrivacyPage } from "../privacy/pages/PrivacyPage";
import { type PrivacyRuntime, PrivacyRuntimeProvider } from "../privacy/runtime";
import { createAdminRequestsRuntime } from "../requests/admin-runtime";
import { RequestsRoutes } from "../requests/RequestsRoutes";
import { type RequestsRuntime, RequestsRuntimeProvider } from "../requests/runtime";
import { createAdminTeamRuntime } from "../team/admin-runtime";
import { StaffTeamPage } from "../team/pages/StaffTeamPage";
import { type TeamRuntime, TeamRuntimeProvider } from "../team/runtime";
import { CoverageRoutes } from "../team/TeamRoutes";
import { AreaGuard } from "./AreaGuard";

// The Refine resources. The four `catalog-*` resources are served by the Lafayette data provider (the
// typed staff catalog API). Customers is the staff memory review (CHT-721). The other areas are
// placeholders until their phases.
const resources = [
  { name: "home", list: "/home" },
  { name: "concierge", list: "/concierge" },
  { name: "customers", list: "/customers" },
  { name: "requests", list: "/requests" },
  { name: "trips", list: "/trips" },
  { name: "actions", list: "/actions" },
  { name: "payments", list: "/payments" },
  { name: "coverage", list: "/coverage" },
  {
    name: "catalog-items",
    list: "/catalog",
    create: "/catalog/new",
    edit: "/catalog/items/:id",
    show: "/catalog/items/:id",
    meta: { label: "Catalog" },
  },
  { name: "catalog-categories", meta: { parent: "catalog-items", label: "Categories" } },
  { name: "catalog-tags", meta: { parent: "catalog-items", label: "Tags" } },
  { name: "catalog-people", meta: { parent: "catalog-items", label: "People" } },
  {
    name: "catalog-imports",
    list: "/imports",
    create: "/imports/new",
    show: "/imports/:id",
    meta: { label: "Imports", dataProviderName: IMPORTS_DATA_PROVIDER },
  },
  { name: "operations", list: "/operations" },
  { name: "staff", list: "/staff" },
];

type AdminApplicationProps = Readonly<{
  /** Tests pass a runtime over a fake API. The real app builds its own from the public config. */
  catalogRuntime?: CatalogRuntime;
  /** The same for the importer: tests pass a runtime over a fake imports API. */
  importsRuntime?: ImportsRuntime;
  /** The same for the customer memory pages: tests pass a runtime over a fake memory API. */
  memoryRuntime?: MemoryRuntime;
  /** The same for the concierge workspace (CHT-732): a fake API and a fake Stream connector. */
  conversationsRuntime?: ConversationsRuntime;
  /** The same for the staff itinerary pages (CHT-747): fake itinerary, catalog and request clients. */
  itineraryRuntime?: ItineraryRuntime;
  /** The same for the staff request pages (CHT-750 to CHT-752): a fake requests API. */
  requestsRuntime?: RequestsRuntime;
  /** The same for the Operations page (CHT-761): a fake operations API. */
  operationsRuntime?: OperationsRuntime;
  /** The same for the diagnostics pages under Operations (CHT-772): fakes of provider health and of the audit and failure reads. */
  diagnosticsRuntime?: DiagnosticsRuntime;
  /** The same for Home and the shell (CHT-767): a fake attention API. */
  homeRuntime?: HomeRuntime;
  /** The same for the Payments screens and the quote panel of a request (CHT-757, CHT-759): fake payment and quote APIs. */
  paymentsRuntime?: PaymentsRuntime;
  /** The same for the team, coverage and customer support screens (CHT-768): a fake team API. */
  teamRuntime?: TeamRuntime;
  /** The same for the privacy requests under Customers (CHT-771): a fake privacy API. */
  privacyRuntime?: PrivacyRuntime;
  /** The same for the Analytics area (CHT-2027): a fake analytics API. */
  analyticsRuntime?: AnalyticsRuntime;
}>;

/** Home, Payments, Team and Analytics share one place in the tree, so each runtime needs no extra level of nesting. */
function HomeAndPaymentsProviders({
  home,
  payments,
  team,
  privacy,
  analytics,
  children,
}: Readonly<{
  home: HomeRuntime;
  payments: PaymentsRuntime;
  team: TeamRuntime;
  privacy: PrivacyRuntime;
  analytics: AnalyticsRuntime;
  children: ReactNode;
}>) {
  return (
    <HomeRuntimeProvider value={home}>
      <PaymentsRuntimeProvider value={payments}>
        <TeamRuntimeProvider value={team}>
          <PrivacyRuntimeProvider value={privacy}>
            <AnalyticsRuntimeProvider value={analytics}>{children}</AnalyticsRuntimeProvider>
          </PrivacyRuntimeProvider>
        </TeamRuntimeProvider>
      </PaymentsRuntimeProvider>
    </HomeRuntimeProvider>
  );
}

/** The development placeholder only in development with its switch on; every other environment uses the real identity provider. */
function authProviderOf(config: ReturnType<typeof getAdminPublicConfig>) {
  return config.developmentAuthPlaceholderEnabled && config.environment === "development"
    ? developmentAuthProvider
    : supabaseAuthProvider;
}

export function AdminApplication({
  catalogRuntime,
  importsRuntime,
  memoryRuntime,
  conversationsRuntime,
  itineraryRuntime,
  requestsRuntime,
  operationsRuntime,
  diagnosticsRuntime,
  homeRuntime,
  paymentsRuntime,
  teamRuntime,
  privacyRuntime,
  analyticsRuntime,
}: AdminApplicationProps = {}) {
  const config = getAdminPublicConfig();
  const authProvider = authProviderOf(config);
  const runtime = useMemo(() => catalogRuntime ?? createAdminCatalogRuntime(), [catalogRuntime]);
  const dataProvider = useMemo(() => createLafayetteDataProvider(runtime.api), [runtime]);
  const imports = useMemo(() => importsRuntime ?? createAdminImportsRuntime(), [importsRuntime]);
  const importsDataProvider = useMemo(() => createImportsDataProvider(imports.api), [imports]);
  const memory = useMemo(() => memoryRuntime ?? createAdminMemoryRuntime(), [memoryRuntime]);
  const conversations = useMemo(
    () => conversationsRuntime ?? createAdminConversationsRuntime(),
    [conversationsRuntime],
  );
  const requests = useMemo(
    () => requestsRuntime ?? createAdminRequestsRuntime(),
    [requestsRuntime],
  );

  const operations = useMemo(
    () => operationsRuntime ?? createAdminOperationsRuntime(),
    [operationsRuntime],
  );
  const diagnostics = useMemo(
    () => diagnosticsRuntime ?? createAdminDiagnosticsRuntime(),
    [diagnosticsRuntime],
  );
  const home = useMemo(() => homeRuntime ?? createAdminHomeRuntime(), [homeRuntime]);
  const payments = useMemo(
    () => paymentsRuntime ?? createAdminPaymentsRuntime(),
    [paymentsRuntime],
  );

  const team = useMemo(() => teamRuntime ?? createAdminTeamRuntime(), [teamRuntime]);
  const privacy = useMemo(() => privacyRuntime ?? createAdminPrivacyRuntime(), [privacyRuntime]);
  const analytics = useAnalyticsRuntimeOf(analyticsRuntime);

  const itinerary = useMemo(
    () => itineraryRuntime ?? createAdminItineraryRuntime(),
    [itineraryRuntime],
  );

  return (
    <AdminAnalyticsProvider>
      <CatalogRuntimeProvider value={runtime}>
        <ImportsRuntimeProvider value={imports}>
          <MemoryRuntimeProvider value={memory}>
            <HomeAndPaymentsProviders
              home={home}
              payments={payments}
              team={team}
              privacy={privacy}
              analytics={analytics}
            >
              <Refine
                authProvider={authProvider}
                dataProvider={{
                  default: dataProvider,
                  [IMPORTS_DATA_PROVIDER]: importsDataProvider,
                }}
                routerProvider={routerProvider}
                resources={resources}
                options={{ syncWithLocation: true, disableTelemetry: true }}
              >
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/auth/callback" element={<AuthCallbackPage />} />
                  <Route element={<ProtectedBoundary />}>
                    <Route index element={<Navigate to="/home" replace />} />
                    <Route element={<AreaGuard />}>
                      <Route path={shellRoutePath("home")} element={<HomePage />} />
                      <Route
                        path={shellRoutePath("concierge")}
                        element={
                          <ConversationsRuntimeProvider value={conversations}>
                            <ConversationsRoutes />
                          </ConversationsRuntimeProvider>
                        }
                      />
                      <Route path={shellRoutePath("customerPrivacy")} element={<PrivacyPage />} />
                      <Route path={shellRoutePath("customers")} element={<CustomersRoutes />} />
                      <Route
                        path={shellRoutePath("tripItinerary")}
                        element={
                          <ItineraryRuntimeProvider value={itinerary}>
                            <ItineraryRoutes />
                          </ItineraryRuntimeProvider>
                        }
                      />
                      <Route
                        path={shellRoutePath("requests")}
                        element={
                          <RequestsRuntimeProvider value={requests}>
                            <RequestsRoutes />
                          </RequestsRuntimeProvider>
                        }
                      />
                      <Route
                        path={shellRoutePath("trips")}
                        element={
                          <PlaceholderPage
                            title="Trips"
                            description="Structured trip and itinerary operations are outside the Phase 1 shell."
                          />
                        }
                      />
                      <Route
                        path={shellRoutePath("actions")}
                        element={
                          <PlaceholderPage
                            title="Actions"
                            description="Customer-visible action projections require the later canonical domain layer."
                          />
                        }
                      />
                      <Route path={shellRoutePath("payments")} element={<PaymentsRoutes />} />
                      <Route path={shellRoutePath("catalog")} element={<CatalogRoutes />} />
                      <Route path={shellRoutePath("imports")} element={<ImportRoutes />} />
                      <Route
                        path={shellRoutePath("operations")}
                        element={
                          <OperationsRuntimeProvider value={operations}>
                            <DiagnosticsRuntimeProvider value={diagnostics}>
                              <OperationsRoutes />
                            </DiagnosticsRuntimeProvider>
                          </OperationsRuntimeProvider>
                        }
                      />
                      {/* One page, so no nested Routes: the pattern is `/staff` and a nested Routes under it makes React Router warn at every open. */}
                      <Route path={shellRoutePath("staff")} element={<StaffTeamPage />} />
                      <Route path={shellRoutePath("coverage")} element={<CoverageRoutes />} />
                      <Route path={shellRoutePath("analytics")} element={<AnalyticsRoutes />} />
                    </Route>
                  </Route>
                  <Route path="*" element={<Navigate to="/home" replace />} />
                </Routes>
              </Refine>
            </HomeAndPaymentsProviders>
          </MemoryRuntimeProvider>
        </ImportsRuntimeProvider>
      </CatalogRuntimeProvider>
    </AdminAnalyticsProvider>
  );
}
